import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Video, Mic, Image as ImageIcon, FileText, Heart, KeyRound, Umbrella,
  Home, Award, UtensilsCrossed, PawPrint, Mail, ShieldCheck, Lock,
  Fingerprint, EyeOff, CloudUpload, UserCheck, BadgeCheck, ArrowRight,
  Check, Menu, X, Sparkles, Facebook, Instagram, Linkedin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from '../components/ui/accordion';
import { AppLogoStatic } from '../components/AppLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IMG = {
  hero: 'https://images.unsplash.com/photo-1742522450616-a2cf0cba1274?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtdWx0aWdlbmVyYXRpb25hbCUyMGZhbWlseSUyMGh1Z2dpbmclMjBoYXBweSUyMG91dGRvb3JzfGVufDB8fHx8MTc4Mjg4NzQyMXww&ixlib=rb-4.1.0&q=85',
  grandmother: 'https://images.unsplash.com/photo-1663429122432-c2769373768f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxncmFuZG1vdGhlciUyMHNtaWxpbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODI4ODc0MjF8MA&ixlib=rb-4.1.0&q=85',
  veteran: 'https://images.unsplash.com/photo-1542328025-50d8b06fc769?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx2ZXRlcmFuJTIwcG9ydHJhaXQlMjB1bmlmb3JtfGVufDB8fHx8MTc4Mjg4NzQyMXww&ixlib=rb-4.1.0&q=85',
  parent: 'https://images.unsplash.com/photo-1758687126380-cc2214b028e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxmYXRoZXIlMjBzaXR0aW5nJTIwYXQlMjBkZXNrJTIwd3JpdGluZ3xlbnwwfHx8fDE3ODI4ODc0Mzd8MA&ixlib=rb-4.1.0&q=85',
  memoryBox: 'https://images.unsplash.com/photo-1453828423292-392a660a502f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBtZW1vcnklMjBib3glMjBvbGQlMjBwaG90b3N8ZW58MHx8fHwxNzgyODg3NDM4fDA&ixlib=rb-4.1.0&q=85',
};

const NAV = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Partners', href: '#partners' },
  { label: 'FAQ', href: '#faq' },
];

const FEATURES = [
  { icon: Video, title: 'Video Messages', text: 'Record heartfelt videos for the moments you can\'t be there for.' },
  { icon: Mic, title: 'Voice Recordings', text: 'Preserve your voice — a lullaby, a story, an "I love you".' },
  { icon: ImageIcon, title: 'Family Photos', text: 'Keep cherished photos safe and organized in one place.' },
  { icon: FileText, title: 'Important Documents', text: 'Store wills, deeds, and paperwork your family will need.' },
  { icon: Heart, title: 'Final Wishes', text: 'Share your preferences clearly, so decisions are never a burden.' },
  { icon: KeyRound, title: 'Password Hints', text: 'Leave secure hints so accounts aren\'t lost forever.' },
  { icon: Umbrella, title: 'Insurance Information', text: 'Life, home, and auto policies — all easy to find.' },
  { icon: Home, title: 'Property Records', text: 'Document homes, vehicles, and valuables in one vault.' },
  { icon: Award, title: 'Military Records', text: 'Safeguard service history and benefits information.' },
  { icon: UtensilsCrossed, title: 'Family Recipes', text: 'Pass down the flavors that made your family feel like home.' },
  { icon: PawPrint, title: 'Pet Care Instructions', text: 'Make sure your beloved companions are always cared for.' },
  { icon: Mail, title: 'Letters to Loved Ones', text: 'Write the words you want them to keep forever.' },
];

const STEPS = [
  { n: '01', title: 'Create your secure account', text: 'Sign up in minutes and set up your private legacy vault.' },
  { n: '02', title: 'Add memories & information', text: 'Upload photos, videos, documents, letters, and important details.' },
  { n: '03', title: 'Choose trusted contacts', text: 'Select the beneficiaries who can access your vault when the time comes.' },
  { n: '04', title: 'Update anytime', text: 'Keep everything current and give your family lasting peace of mind.' },
];

const TRUST = [
  { icon: Lock, title: 'Encrypted storage' },
  { icon: ShieldCheck, title: 'Secure login' },
  { icon: Fingerprint, title: 'Multi-factor authentication' },
  { icon: EyeOff, title: 'Private by default' },
  { icon: CloudUpload, title: 'Secure cloud backups' },
  { icon: UserCheck, title: 'You control who has access' },
  { icon: BadgeCheck, title: 'We never sell your data' },
];

const PLANS = [
  {
    id: 'free', name: 'Free Plan', monthly: '$0', yearly: '$0', cta: 'Start Free', popular: false, aftercare: null,
    features: ['One legacy vault', 'Basic storage', 'One trusted contact', 'Photo & document uploads'],
  },
  {
    id: 'premium', name: 'Premium Plan', monthly: '$9.99', yearly: '$99', save: 'Save $20.88', cta: 'Go Premium', popular: true, aftercare: '1 year after-passing access included',
    features: ['More storage', 'Unlimited letters', 'Video & voice messages', 'Multiple trusted contacts', 'Scheduled message delivery', 'Priority support'],
  },
  {
    id: 'family', name: 'Family Plan', monthly: '$14.99', yearly: '$149', save: 'Save $30.88', cta: 'Start Family Plan', popular: false, aftercare: '3 years after-passing access included',
    features: ['Multiple family members', 'Shared family vault', 'More storage', 'Family timeline', 'Multiple beneficiaries', 'Premium support'],
  },
  {
    id: 'legacy_plus', name: 'Legacy Plus', monthly: '$19.99', yearly: '$199', save: 'Save $40.88', cta: 'Choose Legacy Plus', popular: false, aftercare: '5 years after-passing access included',
    features: ['Everything in Family', 'More storage & Whispers', 'Enhanced beneficiary portal', 'Priority support', 'Lifetime Memorial add-on available ($149)'],
  },
];

const USE_CASES = [
  { img: IMG.grandmother, title: 'A grandmother\'s video messages', text: 'She records birthday wishes and bedtime stories, so her grandchildren always have her voice — for every milestone to come.' },
  { img: IMG.veteran, title: 'A veteran\'s records & instructions', text: 'He safely stores his military records, benefits, and family instructions, so nothing important is ever lost or forgotten.' },
  { img: IMG.parent, title: 'A parent gets organized', text: 'She gathers insurance, property, and emergency information in one place, giving her family clarity when they need it most.' },
];

const FAQS = [
  { q: 'What is EasyPassing?', a: 'EasyPassing is a secure family legacy platform that helps you preserve memories, organize important information, and leave meaningful messages for the people you love — all in one private, encrypted vault.' },
  { q: 'Is my information secure?', a: 'Yes. Your data is protected with encrypted storage, secure login, and optional multi-factor authentication. Everything is private by default, and we never sell your personal data.' },
  { q: 'Who can access my information?', a: 'Only you — unless you choose to grant access. You designate trusted contacts or beneficiaries, and you decide exactly what they can see and when.' },
  { q: 'Can I update my information later?', a: 'Absolutely. Your vault is meant to grow with you. Add, edit, or remove memories and information anytime, from any device.' },
  { q: 'What happens if I cancel?', a: 'You can export or download your information before canceling. We keep your data private and give you control over what happens to it.' },
  { q: 'Can I add family members?', a: 'Yes. Our Family Plan lets multiple family members share a vault, build a family timeline, and manage multiple beneficiaries together.' },
  { q: 'Is this a replacement for a will?', a: 'No. EasyPassing is not a replacement for legal estate planning, a will, or advice from an attorney. It is a secure companion tool for organizing memories, messages, and important information.' },
  { q: 'Can professionals partner with EasyPassing?', a: 'Yes! Estate attorneys, funeral homes, financial advisors, insurance agents, churches, and hospice organizations can partner with us to help the families they serve. Visit the Partners section to get started.' },
];

const cream = '#FDFBF7';
const primary = '#2B4C7E';

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Landing() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const isAuthed = typeof window !== 'undefined' && !!localStorage.getItem('session_token');
  const [billing, setBilling] = useState('yearly');

  const goSignup = () => navigate('/login?mode=signup');

  const handlePlan = (planId) => {
    const token = localStorage.getItem('session_token');
    if (planId !== 'free') localStorage.setItem('pending_plan', planId);
    if (token) {
      navigate(planId === 'free' ? '/membership' : `/membership?plan=${planId}`);
    } else {
      navigate('/login?mode=signup');
    }
  };

  // ----- Forms state -----
  const [checklist, setChecklist] = useState({ name: '', email: '' });
  const [checklistDone, setChecklistDone] = useState(false);
  const [checklistLoading, setChecklistLoading] = useState(false);

  const [partner, setPartner] = useState({ name: '', email: '', organization: '', organization_type: '', message: '' });
  const [partnerDone, setPartnerDone] = useState(false);
  const [partnerLoading, setPartnerLoading] = useState(false);

  const [contact, setContact] = useState({ name: '', email: '', message: '' });
  const [contactDone, setContactDone] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  const submitChecklist = async (e) => {
    e.preventDefault();
    setChecklistLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/checklist-signup`, checklist);
      setChecklistDone(true);
      toast.success('Your checklist is on its way! 💌');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setChecklistLoading(false); }
  };

  const submitPartner = async (e) => {
    e.preventDefault();
    setPartnerLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/partner-inquiry`, partner);
      setPartnerDone(true);
      toast.success('Thank you! Our partnerships team will reach out soon.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setPartnerLoading(false); }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, contact);
      setContactDone(true);
      toast.success('Message sent! We\'ll be in touch.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setContactLoading(false); }
  };

  const inputCls = 'rounded-xl border-slate-200 bg-white focus:border-[#2B4C7E] focus:ring-[#2B4C7E]/20 text-slate-800';

  return (
    <div className="font-body min-h-screen bg-[#FDFBF7] text-slate-800 antialiased">
      {/* ===== NAV ===== */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="#top" className="flex items-center gap-2" data-testid="nav-logo">
              <AppLogoStatic size="sm" showText={false} />
              <span className="font-display text-xl font-bold text-[#2B4C7E]">EasyPassing</span>
            </a>
            <nav className="hidden md:flex items-center gap-8">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} className="text-sm font-medium text-slate-600 hover:text-[#2B4C7E] transition-colors">
                  {n.label}
                </a>
              ))}
            </nav>
            {/* Desktop actions — Sign In + Get Started always visible */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthed && (
                <button onClick={() => navigate('/dashboard')} className="text-sm font-semibold text-[#2B4C7E] hover:underline" data-testid="nav-dashboard">Dashboard</button>
              )}
              <button onClick={() => navigate('/login')} className="text-sm font-semibold text-[#2B4C7E] hover:underline" data-testid="nav-signin">Sign In</button>
              <Button onClick={goSignup} data-testid="nav-get-started" className="rounded-full bg-[#2B4C7E] hover:bg-[#1E3A63] text-white px-6">
                Get Started
              </Button>
            </div>

            {/* Mobile actions — compact, always-visible Sign In + Get Started */}
            <div className="flex md:hidden items-center gap-1.5">
              <button
                onClick={() => navigate('/login')}
                className="h-9 px-3 rounded-full border border-[#2B4C7E] text-[#2B4C7E] text-[13px] font-semibold leading-none whitespace-nowrap flex items-center justify-center"
                data-testid="mobile-signin"
                aria-label="Sign in to EasyPassing"
              >Sign In</button>
              <button
                onClick={goSignup}
                className="h-9 px-3.5 rounded-full bg-[#C8A97E] text-[#1C252E] text-[13px] font-bold leading-none whitespace-nowrap flex items-center justify-center"
                data-testid="mobile-get-started-header"
                aria-label="Get started with EasyPassing"
              >Get Started</button>
              <button className="h-9 w-9 p-0 text-[#2B4C7E] flex items-center justify-center shrink-0" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle" aria-label="Open navigation menu">
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
        {menuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="md:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-3">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="block text-slate-700 font-medium py-1">{n.label}</a>
            ))}
            {isAuthed && (
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }} className="block text-[#2B4C7E] font-semibold py-1">Dashboard</a>
            )}
          </motion.div>
        )}
      </header>

      {/* ===== HERO ===== */}
      <section id="top" className="px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EBF0F5] text-[#2B4C7E] text-sm font-semibold mb-6">
              <Sparkles className="h-4 w-4 text-[#C8A97E]" />
              A gift of love & peace of mind
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1C252E] leading-[1.1]">
              Preserve Your Memories. Protect What Matters.
              <span className="block text-[#C8A97E] mt-2">Leave a Lasting Gift for the People You Love.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              EasyPassing helps you securely store videos, letters, photos, voice messages, documents, final wishes, and important information so your loved ones can access them when they need them most.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button onClick={goSignup} data-testid="hero-get-started" className="rounded-full bg-[#2B4C7E] hover:bg-[#1E3A63] text-white px-8 py-6 text-base font-semibold shadow-sm">
                Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="#how-it-works">
                <Button variant="outline" data-testid="hero-learn-more" className="w-full sm:w-auto rounded-full border-2 border-[#2B4C7E] text-[#2B4C7E] hover:bg-[#EBF0F5] px-8 py-6 text-base font-semibold bg-transparent">
                  Learn How It Works
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#2B4C7E]" /> Encrypted & private</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#2B4C7E]" /> Free to start</span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#EBF0F5] rounded-[2.5rem] -rotate-2" />
              <img src={IMG.hero} alt="A happy multigenerational family embracing outdoors" loading="eager" className="relative rounded-[2rem] shadow-2xl w-full h-[380px] sm:h-[480px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="px-6 lg:px-8 py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Everything Your Family May Need — In One Place</h2>
            <p className="mt-4 text-slate-600">From treasured memories to the practical details, EasyPassing keeps it organized, secure, and ready.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 4) * 0.05}>
                <motion.div whileHover={{ y: -6 }} className="h-full bg-[#FDFBF7] rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(43,76,126,0.1)] transition-all" data-testid={`feature-card-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-[#EBF0F5] flex items-center justify-center mb-4">
                    <f.icon className="h-6 w-6 text-[#2B4C7E]" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#1C252E] mb-1">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.text}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="px-6 lg:px-8 py-20 sm:py-28 bg-[#EBF0F5]">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">How It Works</h2>
            <p className="mt-4 text-slate-600">Getting started takes just a few minutes — and gives you a lifetime of peace of mind.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="relative">
                  <div className="font-display text-6xl font-bold text-[#2B4C7E]/15 leading-none mb-3">{s.n}</div>
                  <h3 className="font-display text-xl font-bold text-[#1C252E] mb-2">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-14">
            <Button onClick={goSignup} data-testid="how-get-started" className="rounded-full bg-[#2B4C7E] hover:bg-[#1E3A63] text-white px-8 py-6 text-base font-semibold">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ===== TRUST & SECURITY ===== */}
      <section id="security" className="px-6 lg:px-8 py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EBF0F5] text-[#2B4C7E] text-sm font-semibold mb-5">
              <ShieldCheck className="h-4 w-4" /> Trust & Security
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Your Legacy, Safely Guarded</h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed border-l-4 border-[#C8A97E] pl-5">
              Your information belongs to you. We protect your privacy and give you control over who can access your legacy vault.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TRUST.map((t) => (
                <div key={t.title} className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-[#EBF0F5] flex items-center justify-center shrink-0">
                    <t.icon className="h-5 w-5 text-[#2B4C7E]" />
                  </div>
                  <span className="font-medium text-slate-700 text-sm">{t.title}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== LEAD MAGNET ===== */}
      <section className="px-6 lg:px-8 py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="rounded-[2.5rem] bg-[#2B4C7E] text-white overflow-hidden grid lg:grid-cols-2">
              <div className="p-8 sm:p-12">
                <p className="text-[#C8A97E] font-semibold uppercase tracking-wider text-sm mb-3">Free Download</p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">The 50 Things Every Family Should Know</h2>
                <p className="mt-4 text-white/80 leading-relaxed">A thoughtful checklist to help you and your family prepare, organize, and find peace of mind — no matter what tomorrow brings.</p>
                {checklistDone ? (
                  <div className="mt-8 flex items-center gap-3 bg-white/10 rounded-2xl p-5" data-testid="checklist-success">
                    <BadgeCheck className="h-6 w-6 text-[#C8A97E]" />
                    <p className="text-white/90">Wonderful! Your checklist is on its way to your inbox.</p>
                  </div>
                ) : (
                  <form onSubmit={submitChecklist} className="mt-8 space-y-4">
                    <Input required placeholder="Your name" value={checklist.name} onChange={(e) => setChecklist({ ...checklist, name: e.target.value })} className="rounded-xl bg-white/95 border-0 text-slate-800 placeholder:text-slate-400 h-12" data-testid="checklist-name" />
                    <Input required type="email" placeholder="Your email" value={checklist.email} onChange={(e) => setChecklist({ ...checklist, email: e.target.value })} className="rounded-xl bg-white/95 border-0 text-slate-800 placeholder:text-slate-400 h-12" data-testid="checklist-email" />
                    <Button type="submit" disabled={checklistLoading} className="w-full rounded-full bg-[#C8A97E] hover:bg-[#B3956B] text-white h-12 font-semibold" data-testid="checklist-submit">
                      {checklistLoading ? 'Sending...' : 'Send Me the Checklist'}
                    </Button>
                  </form>
                )}
              </div>
              <div className="relative min-h-[260px] hidden lg:block">
                <img src={IMG.memoryBox} alt="A family memory box filled with old photographs" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="px-6 lg:px-8 py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Simple, Honest Pricing</h2>
            <p className="mt-4 text-slate-600">Start free. Upgrade whenever you're ready. Cancel anytime.</p>
            <div className="mt-8 flex flex-col items-center gap-3">
              <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm" data-testid="landing-billing-toggle">
                {[['monthly', 'Monthly'], ['yearly', 'Yearly']].map(([v, label]) => (
                  <button key={v} onClick={() => setBilling(v)} data-testid={`landing-billing-${v}`}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${billing === v ? 'bg-[#2B4C7E] text-white' : 'text-slate-600'}`}>
                    {label}{v === 'yearly' && <span className="ml-1.5 text-xs font-bold" style={{ color: billing === 'yearly' ? '#F4D9A0' : '#C8A97E' }}>Save up to 17%</span>}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <div className={`relative h-full bg-white rounded-3xl p-8 border shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col ${p.popular ? 'border-2 border-[#C8A97E]' : 'border-slate-100'}`} data-testid={`pricing-card-${i}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8A97E] text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">Most Popular</span>
                  )}
                  <h3 className="font-display text-xl font-bold text-[#1C252E]">{p.name}</h3>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="font-display text-4xl font-bold text-[#2B4C7E]" data-testid={`pricing-price-${i}`}>{billing === 'yearly' ? p.yearly : p.monthly}</span>
                    <span className="text-slate-500 mb-1">{p.id === 'free' ? '/month' : (billing === 'yearly' ? '/year' : '/month')}</span>
                  </div>
                  {p.id !== 'free' && billing === 'yearly' && p.save && <p className="mt-1 text-xs font-medium text-[#7FA98C]">{p.save} vs monthly</p>}
                  {p.id !== 'free' && billing === 'monthly' && p.save && <p className="mt-1 text-xs font-medium text-slate-400">or {p.yearly}/year — {p.save.toLowerCase()}</p>}
                  {p.aftercare && <p className="mt-3 text-xs font-semibold rounded-lg px-3 py-2 bg-[#EBF0F5] text-[#1E3A63]" data-testid={`pricing-aftercare-${i}`}>{p.aftercare}</p>}
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                        <Check className="h-5 w-5 text-[#2B4C7E] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => handlePlan(p.id)} data-testid={`pricing-cta-${i}`} className={`mt-8 w-full rounded-full font-semibold py-6 ${p.popular ? 'bg-[#C8A97E] hover:bg-[#B3956B] text-white' : 'bg-[#2B4C7E] hover:bg-[#1E3A63] text-white'}`}>
                    {p.cta}
                  </Button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== USE CASES ===== */}
      <section className="px-6 lg:px-8 py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Ways Families Use EasyPassing</h2>
            <p className="mt-4 text-slate-600">Illustrative examples of how EasyPassing brings families closer and calmer.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {USE_CASES.map((u, i) => (
              <Reveal key={u.title} delay={i * 0.08}>
                <div className="h-full bg-[#FDFBF7] rounded-3xl overflow-hidden border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`use-case-${i}`}>
                  <div className="relative h-56">
                    <img src={u.img} alt={u.title} loading="lazy" className="w-full h-full object-cover" />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur text-[#2B4C7E] text-xs font-bold px-3 py-1 rounded-full">Example</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold text-[#1C252E] mb-2">{u.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{u.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PARTNERS ===== */}
      <section id="partners" className="px-6 lg:px-8 py-20 sm:py-28 bg-[#EBF0F5]">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Partner With EasyPassing</h2>
            <p className="mt-4 text-slate-600">Help your clients and families prepare, preserve memories, and organize important information before it is urgently needed.</p>
            <p className="mt-3 text-sm text-slate-500">For estate attorneys, funeral homes, financial advisors, insurance agents, churches, and hospice organizations.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
              {partnerDone ? (
                <div className="text-center py-8" data-testid="partner-success">
                  <BadgeCheck className="h-12 w-12 text-[#2B4C7E] mx-auto mb-4" />
                  <h3 className="font-display text-xl font-bold text-[#1C252E]">Thank you for your interest!</h3>
                  <p className="text-slate-600 mt-2">Our partnerships team will reach out to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={submitPartner} className="grid sm:grid-cols-2 gap-4">
                  <Input required placeholder="Your name" value={partner.name} onChange={(e) => setPartner({ ...partner, name: e.target.value })} className={inputCls + ' h-12'} data-testid="partner-name" />
                  <Input required type="email" placeholder="Work email" value={partner.email} onChange={(e) => setPartner({ ...partner, email: e.target.value })} className={inputCls + ' h-12'} data-testid="partner-email" />
                  <Input placeholder="Organization" value={partner.organization} onChange={(e) => setPartner({ ...partner, organization: e.target.value })} className={inputCls + ' h-12'} data-testid="partner-org" />
                  <select value={partner.organization_type} onChange={(e) => setPartner({ ...partner, organization_type: e.target.value })} className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-slate-700 focus:border-[#2B4C7E] focus:outline-none" data-testid="partner-type">
                    <option value="">Organization type…</option>
                    <option>Estate Attorney</option>
                    <option>Funeral Home</option>
                    <option>Financial Advisor</option>
                    <option>Insurance Agency</option>
                    <option>Church / Faith Community</option>
                    <option>Hospice Organization</option>
                    <option>Other</option>
                  </select>
                  <Textarea placeholder="Tell us a little about your organization (optional)" value={partner.message} onChange={(e) => setPartner({ ...partner, message: e.target.value })} className={inputCls + ' sm:col-span-2 min-h-[100px]'} data-testid="partner-message" />
                  <Button type="submit" disabled={partnerLoading} className="sm:col-span-2 rounded-full bg-[#2B4C7E] hover:bg-[#1E3A63] text-white h-12 font-semibold" data-testid="partner-submit">
                    {partnerLoading ? 'Sending...' : 'Become a Partner'}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-6 bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.05)] flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left" data-testid="ambassador-cta">
              <div className="flex-1">
                <h3 className="font-display text-xl font-bold text-[#1C252E]">Become an Ambassador</h3>
                <p className="text-slate-600 mt-2">Love EasyPassing? Join our ambassador program, share your referral link, and earn a bonus plus recurring commission for every family you help.</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button onClick={() => navigate('/ambassador/apply')} className="rounded-full bg-[#C9A24B] hover:bg-[#b08f3f] text-white h-12 px-7 font-semibold" data-testid="ambassador-apply-cta">Apply now</Button>
                <button onClick={() => navigate('/ambassador/login')} className="text-sm text-[#2B4C7E] font-semibold hover:underline" data-testid="ambassador-login-cta">Ambassador login</button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="px-6 lg:px-8 py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Frequently Asked Questions</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-slate-100 px-5" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display font-semibold text-[#1C252E] hover:text-[#2B4C7E] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="px-6 lg:px-8 py-20 sm:py-24 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Have a Question? Say Hello.</h2>
            <p className="mt-4 text-slate-600 mb-8">We're here to help you and your family every step of the way.</p>
          </Reveal>
          <Reveal delay={0.1}>
            {contactDone ? (
              <div className="bg-[#FDFBF7] rounded-3xl p-8 border border-slate-100" data-testid="contact-success">
                <BadgeCheck className="h-12 w-12 text-[#2B4C7E] mx-auto mb-4" />
                <h3 className="font-display text-xl font-bold text-[#1C252E]">Message sent!</h3>
                <p className="text-slate-600 mt-2">Thank you for reaching out — we'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={submitContact} className="bg-[#FDFBF7] rounded-3xl p-6 sm:p-8 border border-slate-100 space-y-4 text-left">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input required placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={inputCls + ' h-12'} data-testid="contact-name" />
                  <Input required type="email" placeholder="Your email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls + ' h-12'} data-testid="contact-email" />
                </div>
                <Textarea required placeholder="How can we help?" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className={inputCls + ' min-h-[120px]'} data-testid="contact-message" />
                <Button type="submit" disabled={contactLoading} className="w-full rounded-full bg-[#2B4C7E] hover:bg-[#1E3A63] text-white h-12 font-semibold" data-testid="contact-submit">
                  {contactLoading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="px-6 lg:px-8 pb-8">
        <div className="max-w-6xl mx-auto rounded-[2.5rem] bg-gradient-to-br from-[#2B4C7E] to-[#1E3A63] text-white text-center px-8 py-16 sm:py-20">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Give Your Family the Gift of Peace of Mind</h2>
            <p className="mt-4 text-white/80 max-w-xl mx-auto">It's never too early to start. Create your free legacy vault today.</p>
            <Button onClick={goSignup} data-testid="final-get-started" className="mt-8 rounded-full bg-[#C8A97E] hover:bg-[#B3956B] text-white px-8 py-6 text-base font-semibold">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#2B4C7E] text-white px-6 lg:px-8 pt-16 pb-8">
        <div className="max-w-7xl mx-auto grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <AppLogoStatic size="sm" showText={false} />
              <span className="font-display text-xl font-bold">EasyPassing.life</span>
            </div>
            <p className="text-white/70 max-w-sm text-sm leading-relaxed">
              A secure family legacy platform to preserve memories, organize important information, and leave meaningful messages for the people you love.
            </p>
            <div className="flex gap-3 mt-5">
              {[Facebook, Instagram, Linkedin].map((Icon, i) => (
                <a key={i} href="#top" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid={`footer-social-${i}`} aria-label="Social link">
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-display font-bold mb-4">Explore</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href="#top" className="hover:text-white">Home</a></li>
              <li><a href="#how-it-works" className="hover:text-white">How It Works</a></li>
              <li><a href="#features" className="hover:text-white">Features</a></li>
              <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              <li><a href="#partners" className="hover:text-white">Partner Program</a></li>
              <li><Link to="/ambassador/apply" className="hover:text-white" data-testid="footer-ambassador-apply">Become an Ambassador</Link></li>
              <li><Link to="/ambassador/login" className="hover:text-white" data-testid="footer-ambassador-login">Ambassador Login</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href="#contact" className="hover:text-white">Contact</a></li>
              <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-white/15 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-white/60">
          <p>© {new Date().getFullYear()} EasyPassing.life. All rights reserved.</p>
          <p className="text-white/50">EasyPassing is a companion tool — not a substitute for legal estate planning.</p>
        </div>
      </footer>
    </div>
  );
}
